import React, { useEffect, useState } from 'react';

// Divergence Alpha — "Populating the Divergence Box" (brief v1.3).
//
// Replaces the static DP explainer with three Simulated Alpha surfaces:
//   1. Against-the-Grain Rate — % of times the viewer disagreed with
//      majority consensus and won. Anchors the user's self-story.
//   2. Reputation Alpha sparkline — RP earned via divergence plays vs
//      RP from standard pledges, rolling window. Shows *where* the
//      user's edge comes from.
//   3. Prophecy Leaderboard — top 3 Citizens who correctly called a
//      "Low Confidence" event, i.e. bought into a headline while its
//      CS band was still volatile and the market paid out on it.
//
// All three panels are simulated for the sandbox (no backing data in
// Postgres/Supabase yet). Values jitter slightly every ALPHA_TICK_MS
// so the terminal feels live. The simulated flag is rendered front
// and centre so testers never mistake this for real alpha.

const ALPHA_TICK_MS = 8_000;

// ─── Helpers ────────────────────────────────────────────────────────────

function jitter(base: number, amp: number, lo: number, hi: number): number {
  const next = base + (Math.random() * 2 - 1) * amp;
  return Math.max(lo, Math.min(hi, next));
}

function clampRound(n: number): number {
  return Math.round(n * 10) / 10;
}

// ─── Sparkline (pure SVG, no library) ───────────────────────────────────

interface SparklineProps {
  values: number[];
  color:  string;
  width:  number;
  height: number;
}

function Sparkline({ values, color, width, height }: SparklineProps): React.JSX.Element {
  if (values.length < 2) {
    return <svg width={width} height={height} />;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const rng = Math.max(1e-6, max - min);
  const stepX = width / (values.length - 1);
  const pts = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / rng) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth={1.4}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ─── Stat tile (v1.3.2) ─────────────────────────────────────────────────

interface StatTileProps {
  label:  string;
  value:  string;
  accent: string;
  hint:   string;
}

function StatTile({ label, value, accent, hint }: StatTileProps): React.JSX.Element {
  return (
    <div style={{
      padding:         '8px 10px',
      backgroundColor: '#0f172a',
      border:          '1px solid #333',
      borderRadius:    6,
      display:         'flex',
      flexDirection:   'column',
      gap:             3,
    }}>
      <p style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em', margin: 0, fontWeight: 700 }}>
        {label}
      </p>
      <span style={{ color: accent, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>
        {value}
      </span>
      <p style={{ color: '#334155', fontSize: 8, letterSpacing: '0.14em', margin: 0 }}>
        {hint}
      </p>
    </div>
  );
}

// ─── Seed data ──────────────────────────────────────────────────────────

interface ProphecyRow {
  citizen: string;
  calls:   number;  // low-confidence calls that paid out
  hitRate: number;  // 0..1
}

const SEED_PROPHECY: ProphecyRow[] = [
  { citizen: 'citizen_A7F2', calls: 14, hitRate: 0.79 },
  { citizen: 'citizen_3C91', calls: 11, hitRate: 0.73 },
  { citizen: 'citizen_88B4', calls:  9, hitRate: 0.67 },
];

function seedSparkline(n: number, lo: number, hi: number): number[] {
  const out: number[] = [];
  let v = (lo + hi) / 2;
  for (let i = 0; i < n; i++) {
    v = jitter(v, (hi - lo) * 0.15, lo, hi);
    out.push(v);
  }
  return out;
}

// ─── Component ──────────────────────────────────────────────────────────

export default function DivergenceAlphaBox(): React.JSX.Element {
  const [grainRate,  setGrainRate]  = useState(54.8);   // %
  const [divSeries,  setDivSeries]  = useState<number[]>(() => seedSparkline(24, 20, 85));
  const [stdSeries,  setStdSeries]  = useState<number[]>(() => seedSparkline(24, 30, 60));
  const [prophecy,   setProphecy]   = useState<ProphecyRow[]>(SEED_PROPHECY);

  // Tile-strip state (brief v1.3.2). Seeded inside the briefed ranges so
  // the first render already shows plausible numbers, then jittered on
  // each tick to match the cadence of the panels below.
  const [currentAlpha,  setCurrentAlpha]  = useState(() => 10 + Math.random() * 30);  // 10..40 %
  const [contrarianWins, setContrarianWins] = useState(() => 1 + Math.floor(Math.random() * 5)); // 1..5
  const [repViaDiv,     setRepViaDiv]     = useState(() => 50 + Math.random() * 150);  // 50..200 RP

  useEffect(() => {
    const tick = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      setGrainRate(prev => clampRound(jitter(prev, 0.8, 40, 72)));
      setDivSeries(prev => [...prev.slice(1), jitter(prev[prev.length - 1], 6, 20, 95)]);
      setStdSeries(prev => [...prev.slice(1), jitter(prev[prev.length - 1], 3, 25, 65)]);
      setProphecy(prev => prev.map(p => ({
        ...p,
        hitRate: Math.max(0.5, Math.min(0.92, p.hitRate + (Math.random() - 0.5) * 0.012)),
      })));
      setCurrentAlpha(prev => jitter(prev, 1.5, 10, 40));
      // Contrarian Wins drifts rarely — a hard integer step each ~4 ticks
      // keeps the badge from flickering every cycle.
      setContrarianWins(prev => {
        if (Math.random() > 0.25) return prev;
        const delta = Math.random() < 0.5 ? -1 : 1;
        return Math.max(1, Math.min(5, prev + delta));
      });
      setRepViaDiv(prev => jitter(prev, 4, 50, 200));
    }, ALPHA_TICK_MS);
    return () => window.clearInterval(tick);
  }, []);

  const divLast = divSeries[divSeries.length - 1];
  const stdLast = stdSeries[stdSeries.length - 1];
  const alpha   = divLast - stdLast;
  const alphaUp = alpha >= 0;

  return (
    <div style={{
      padding:         '14px 16px',
      backgroundColor: '#0b1220',
      border:          '1px solid #333',
      borderRadius:    8,
      fontFamily:      'monospace',
      position:        'relative',
      zIndex:          0,
      overflow:        'hidden',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <p style={{ color: '#475569', fontSize: 9, letterSpacing: '0.22em', margin: 0, fontWeight: 700 }}>
          ◈ DIVERGENCE · SIMULATED ALPHA
        </p>
        <span style={{ flex: 1 }} />
        <span style={{
          color:           '#78350f',
          fontSize:        7,
          letterSpacing:   '0.22em',
          fontWeight:      800,
          padding:         '1px 6px',
          border:          '1px solid #78350f',
          borderRadius:    3,
          backgroundColor: 'rgba(120,53,15,0.12)',
        }}>
          SIMULATED
        </span>
      </div>

      {/* ─ Tile strip (v1.3.2): Current Alpha · Contrarian Wins · Rep via Divergence ─ */}
      <div style={{
        display:             'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap:                 10,
        marginBottom:        12,
      }}>
        <StatTile
          label="CURRENT ALPHA"
          value={`${currentAlpha.toFixed(1)}%`}
          accent="#86efac"
          hint="EDGE VS CROWD"
        />
        <StatTile
          label="CONTRARIAN WINS"
          value={`${contrarianWins}`}
          accent="#fcd34d"
          hint="THIS ROLLING WINDOW"
        />
        <StatTile
          label="RP VIA DIVERGENCE"
          value={`${Math.round(repViaDiv)}`}
          accent="#7dd3fc"
          hint="REPUTATION EARNED"
        />
      </div>

      <div style={{
        display:             'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        gap:                 12,
        alignItems:          'stretch',
      }}>
        {/* ─ Against-the-Grain Rate ───────────────────────────── */}
        <div style={{
          padding:         '10px 12px',
          backgroundColor: '#0f172a',
          border:          '1px solid #333',
          borderRadius:    6,
          display:         'flex',
          flexDirection:   'column',
          gap:             6,
          minWidth:        0,
          position:        'relative',
          zIndex:          1,
        }}>
          <p style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em', margin: 0, fontWeight: 700 }}>
            AGAINST-THE-GRAIN RATE
          </p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ color: '#fcd34d', fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' }}>
              {grainRate.toFixed(1)}%
            </span>
            <span style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.12em' }}>
              WINS VS MAJORITY
            </span>
          </div>
          <div style={{
            height:          4,
            borderRadius:    2,
            backgroundColor: '#1e293b',
            overflow:        'hidden',
          }}>
            <div style={{
              height:          '100%',
              width:           `${grainRate}%`,
              backgroundColor: '#fcd34d',
              transition:      'width 0.6s ease',
            }} />
          </div>
          <p style={{ color: '#334155', fontSize: 9, lineHeight: 1.4, margin: 0 }}>
            % of resolved markets where you disagreed with the crowd and the Oracle settled your way.
          </p>
        </div>

        {/* ─ Reputation Alpha ─────────────────────────────────── */}
        <div style={{
          padding:         '10px 12px',
          backgroundColor: '#0f172a',
          border:          '1px solid #333',
          borderRadius:    6,
          display:         'flex',
          flexDirection:   'column',
          gap:             6,
          minWidth:        0,
          position:        'relative',
          zIndex:          1,
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <p style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em', margin: 0, fontWeight: 700 }}>
              REPUTATION ALPHA
            </p>
            <span style={{ flex: 1 }} />
            <span style={{
              color:      alphaUp ? '#86efac' : '#fca5a5',
              fontSize:   10,
              fontWeight: 700,
            }}>
              {alphaUp ? '▲' : '▼'} {Math.abs(alpha).toFixed(1)} RP
            </span>
          </div>
          <div style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
            <Sparkline values={divSeries} color="#86efac" width={180} height={34} />
            <Sparkline values={stdSeries} color="#64748b" width={180} height={22} />
          </div>
          <div style={{ display: 'flex', gap: 10, fontSize: 8, letterSpacing: '0.12em' }}>
            <span style={{ color: '#86efac' }}>■ DIVERGENCE</span>
            <span style={{ color: '#64748b' }}>■ STANDARD</span>
          </div>
        </div>

        {/* ─ Prophecy Leaderboard ─────────────────────────────── */}
        <div style={{
          padding:         '10px 12px',
          backgroundColor: '#0f172a',
          border:          '1px solid #333',
          borderRadius:    6,
          display:         'flex',
          flexDirection:   'column',
          gap:             6,
          minWidth:        0,
          position:        'relative',
          zIndex:          1,
        }}>
          <p style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em', margin: 0, fontWeight: 700 }}>
            PROPHECY LEADERBOARD
          </p>
          <p style={{ color: '#334155', fontSize: 8, letterSpacing: '0.12em', margin: 0 }}>
            CITIZENS WHO NAILED LOW-CONFIDENCE CALLS
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 2, maxHeight: 120, overflowY: 'auto' }}>
            {prophecy.map((p, i) => {
              const medal = ['🥇', '🥈', '🥉'][i] ?? '·';
              const acc   = ['#fcd34d', '#cbd5e1', '#d97706'][i] ?? '#64748b';
              return (
                <div key={p.citizen} style={{
                  display:     'flex',
                  alignItems:  'center',
                  gap:         6,
                  fontSize:    10,
                }}>
                  <span style={{ width: 14 }}>{medal}</span>
                  <span style={{ color: acc, fontWeight: 700, letterSpacing: '0.06em' }}>
                    {p.citizen}
                  </span>
                  <span style={{ flex: 1 }} />
                  <span style={{ color: '#94a3b8' }}>
                    {p.calls} calls
                  </span>
                  <span style={{ color: acc, fontWeight: 700 }}>
                    {(p.hitRate * 100).toFixed(0)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <p style={{ color: '#1e293b', fontSize: 9, letterSpacing: '0.1em', margin: '12px 0 0', textAlign: 'center' }}>
        Divergence rewards correctness against consensus. Values simulated for the sandbox.
      </p>
    </div>
  );
}
